<template>
  <b-table
    ref="table"
    class="assets-table"
    :data="assets"
    :checkable="false"
    :loading="loading"
    backend-sorting
    :default-sort="[internalSortField, sortDirection]"
    @sort="onSort">
    <template slot-scope="props" slot="header">
      <svgicon v-if="props.column.label === 'Machine'" name="apple" height="20" width="20">
      </svgicon>
      <span v-else
        :class="{ 'is-offset': props.column.label === 'Label' || props.column.label === 'Tags' }">
        {{ props.column.label }}
      </span>
    </template>
    <template slot-scope="props">
      <b-table-column label="Machine" class="machine-icon" centered>
        <svgicon
          :name="$options.filters.macModelImage(props.row.modelName)"
          height="20"
          width="20"
          :class="$options.filters.macModelImage(props.row.modelName)">
        </svgicon>
      </b-table-column>
      <b-table-column field="version" label="Version" centered sortable>
        <span
          @click="$emit('asset-table-version-click', { version: props.row.version })"
          class="tag is-rounded version">
          {{ props.row.version }}
        </span>
      </b-table-column>
      <b-table-column field="hostName" label="Host Name" sortable>
         <router-link :to="{ name: 'assets.index.dashboard', params: { id: props.row.uuid }}">
          <span v-if="props.row.hostName !== null">{{ props.row.hostName }}</span>
          <span v-else>unknown hostname</span>
        </router-link>
      </b-table-column>
      <!-- <b-table-column label="Insights" class="insights" centered>
        <pass-fail-icon :status="true" size="20">
        </pass-fail-icon>
      </b-table-column> -->
      <b-table-column field="label" label="Label" sortable>
        <span class="level edit-right">
          <span class="level-left">
            <edit-popup
              :ref="editLabelPopupRef(props.row.uuid)"
              :id="props.row.uuid"
              :autoHide="false"
              v-on:editable-open="focusInput(labelInputRef(props.row.uuid))"
              class="level-item">
              <div class="level">
                <b-input
                  v-model="internalLabels[props.row.uuid]"
                  :ref="labelInputRef(props.row.uuid)"
                  placeholder="label name"
                  icon-pack="mdi"
                  icon="label"
                  @blur="saveLabel($event, props.row.uuid)"
                  @keyup.enter.native="saveLabel($event, props.row.uuid)">
                </b-input>
                <a @click="$refs[editLabelPopupRef(props.row.uuid)].closePopup()"
                  role="button"
                  class="delete level-item tags-close"></a>
              </div>
            </edit-popup>
            <span class="level-item">{{ props.row.label }}</span>
          </span>
        </span>
      </b-table-column>
      <b-table-column field="checkin" label="Last Checkin" class="date" sortable>
        <span v-if="props.row.checkin">
          {{ props.row.checkin | formatEpochDate(gmt) }}
        </span>
        <span v-else>-</span>
      </b-table-column>
      <b-table-column field="profile_id" label="Profile" sortable>
        <router-link
          v-if="props.row.profile"
          :to="{ name: 'profiles.index.detail', params: { id: props.row.profile.id }}">
          {{ props.row.profile.name }}
        </router-link>
        <span v-else>None</span>
      </b-table-column>
      <b-table-column field="macosVersion" label="macOS" sortable>
        {{ props.row.osMajor }}.{{ props.row.osMinor }}.{{ props.row.osPatch }}
      </b-table-column>
      <b-table-column label="Tags" class="tags-column">
        <span class="level edit-right">
          <span class="level-left">
            <edit-popup
              :ref="editTagsPopupRef(props.row.uuid)"
              :id="props.row.uuid"
              :autoHide="false"
              v-on:editable-open="tagInputOpened(props.row.uuid)"
              class="level-item">
              <div class="level">
                <b-taginput
                  v-model="internalTags[props.row.uuid]"
                  :ref="tagInputRef(props.row.uuid)"
                  :data="tagAutoComplete"
                  autocomplete
                  :allow-new="true"
                  field="value"
                  icon="tag"
                  placeholder="Add a tag"
                  @typing="getFilteredTags"
                  @input="saveTags($event, props.row.uuid)"
                  class="level-item">
                </b-taginput>
                <a @click="$refs[editTagsPopupRef(props.row.uuid)].closePopup()"
                  role="button"
                  class="delete level-item tags-close">
                </a>
              </div>
            </edit-popup>
            <div class="tags">
              <span
                v-for="tag in props.row.tags"
                :key="tag"
                @click="$emit('asset-table-tag-click', { tag })"
                class="tag clickable is-small">
                {{ tag }}
              </span>
            </div>
          </span>
        </span>
      </b-table-column>
    </template>
  </b-table>
</template>

<script>
import _ from 'lodash';
import gmt from '@/mixins/gmt';
import EditPopup from '@/components/EditPopup.vue';

export default {
  name: 'AssetsTable',
  mixins: [gmt],
  components: {
    EditPopup,
  },
  props: {
    assets: {
      type: Array,
      required: true,
    },
    sortField: {
      type: String,
      required: true,
    },
    sortDirection: {
      type: String,
      required: true,
    },
    onSort: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      currentLabel: '',
      tags: [],
      allTags: [],
      currentTags: [],
      currentTagInput: '',
      labelSearch: null,
      isOpen: false,
      internalSortField: this.sortField,
      loading: false,
      activeLabelEditPopUp: null,
      internalLabels: _.mapValues(_.keyBy(this.assets, 'uuid'), 'label'),
      internalTags: _.mapValues(_.keyBy(this.assets, 'uuid'), 'tags'),
      popupOpenTmp: true,
    };
  },
  async mounted() {
    this.refreshAllTags();
  },
  computed: {
    tagAutoComplete() {
      // Exclude tags that are already selected
      const tags = _.filter(this.allTags, t => !this.currentTags.includes(t));

      // Filter by any input
      return _.filter(tags, t => t.startsWith(this.currentTagInput));
    },
  },
  methods: {
    getAsset(uuid) {
      const index = _.findIndex(this.assets, { uuid });
      return this.assets[index];
    },
    sleep(time) {
      return new Promise(resolve => setTimeout(resolve, time));
    },
    focusInput(ref) {
      // This is silly but the click for the pop-up takes focus away from the input
      // So sleep a bit so that the click takes focus then, reset it to the input
      this.sleep(100).then(() => {
        this.$refs[ref].focus();
      });
    },
    tagInputRef(uuid) {
      return `tag-input-${uuid}`;
    },
    editLabelPopupRef(uuid) {
      return `editLabel-${uuid}`;
    },
    editTagsPopupRef(uuid) {
      return `editTags-${uuid}`;
    },
    labelInputRef(uuid) {
      return `label-input-${uuid}`;
    },
    tagInputOpened(uuid) {
      const asset = this.getAsset(uuid);
      this.currentTags = asset.tags;
      // this.tags = _.filter(this.allTags, t => !asset.tags.includes(t));
      this.focusInput(this.tagInputRef(uuid));
    },
    async saveLabel(event, uuid) {
      const index = _.findIndex(this.assets, { uuid });
      if (this.assets[index].label === this.internalLabels[uuid]) {
        return;
      }
      const asset = await this.$store.dispatch('primary/updateAsset', {
        uuid,
        label: this.internalLabels[uuid],
      });
      if (asset) {
        // this.onSort(this.sortField, this.sortDirection);
        // const index = _.findIndex(this.assets, { uuid });
        this.assets[index].label = asset.label;
        // This is a bit ghetto but not sure of a better way to signal a close event
        // to the edit-input-popup is submitted via an enter key
        this.$refs[this.editLabelPopupRef(uuid)].closePopup();
      }
    },
    async saveTags(tags, uuid) {
      const asset = await this.$store.dispatch('primary/updateAsset', {
        uuid,
        tags,
      });
      if (asset) {
        const index = _.findIndex(this.assets, { uuid });
        this.assets[index].tags = asset.tags;
        this.currentTags = asset.tags;
        this.refreshAllTags();
      }
    },
    async refreshAllTags() {
      const tags = await this.$store.dispatch('primary/listAssetsStats', {
        field: 'tags',
      });
      if (tags) {
        this.allTags = _.map(tags, 'value');
      }
    },
    getFilteredTags(input) {
      // This will trigger tagAutoComplete to be recomputed
      this.currentTagInput = input;
    },
    test() {
      this.isOpen = true;
    },
    addTag(tag, uuid) {
      this.internalTags[uuid].push(tag);
    },
    removeTag(tag, uuid) {
      this.internalTags[uuid] = this.internalTags[uuid].filter(t => t !== tag);
    },
  },
};
</script>

<style lang="scss" scoped>

.tags-column {
  min-width: 18rem;
}

.tag {
  &.version {
    cursor: pointer;
    background: none;
    border: 2px solid $border-color;
  }
}

.tags-close {
  margin-left: .5rem;
}

.b-table .level {
  padding-bottom: 0;
}

.level:not(:last-child) {
  margin-bottom: 0;
}

img.header {
  width: $table-icon-size;
  height: $table-icon-size;
}

.level-right .level-item:not(:last-child) {
  margin-right: 0;
}

input.input {
  margin-bottom: .2rem;
  width: 15rem;
}

div.assets-table {

  .is-offset {
    padding-left: 1.2rem; // Offset for label edit button
  }

  span.edit-right {
    padding-bottom: 0;
  }

  td.machine-icon,
  td.insights {
    width: $table-icon-size;
    text-align: center;
    img {
      width: $table-icon-size;
      max-width: $table-icon-size;
      height: $table-icon-size;
    }
  }

  th.date, td.date {
    width: $table-date-width;
  }
}

</style>
